import React from 'react';
import API from './api';
import './CredentialRenewalHolderPortal.css';

const INVITATION_ERROR = 'This invitation is invalid, expired, revoked, or already used. Ask the requesting organisation for a new invitation.';

function invitationParameters() {
  const query = new URLSearchParams(window.location.search);
  return { tenantId: query.get('tenant') || '', token: query.get('token') || '' };
}

function portalHeaders(tenantId, portalToken) {
  return {
    Authorization: `Portal ${portalToken}`,
    'X-VISINVOKE-Portal-Tenant': tenantId,
  };
}

function idempotencyKey() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  return `crmgr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function sha256Hex(file) {
  if (!file || !window.crypto?.subtle) throw new Error('secure_hash_unavailable');
  const bytes = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
}

export default function CredentialRenewalHolderPortal() {
  const [tenantId, setTenantId] = React.useState('');
  const [portalToken, setPortalToken] = React.useState('');
  const [snapshot, setSnapshot] = React.useState(null);
  const [state, setState] = React.useState('accepting');
  const [message, setMessage] = React.useState('Checking your secure invitation...');
  const [selectedLifecycleId, setSelectedLifecycleId] = React.useState('');
  const [privacyClass, setPrivacyClass] = React.useState('standard');
  const [file, setFile] = React.useState(null);
  const [privacyAccepted, setPrivacyAccepted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const loadSnapshot = React.useCallback(async (tenant, token) => {
    const response = await API.get('/v1/credential-renewal/portal/snapshot', {
      headers: portalHeaders(tenant, token),
    });
    setSnapshot(response.data);
  }, []);

  React.useEffect(() => {
    let live = true;
    (async () => {
      const invitation = invitationParameters();
      if (!invitation.tenantId || !invitation.token) {
        if (live) { setState('error'); setMessage(INVITATION_ERROR); }
        return;
      }
      try {
        const response = await API.post('/v1/credential-renewal/portal/invitations/consume', invitation);
        const token = response.data?.portalToken;
        if (!token) throw new Error('missing portal token');
        window.history.replaceState({}, document.title, window.location.pathname + (window.location.hash || ''));
        if (!live) return;
        setTenantId(invitation.tenantId);
        setPortalToken(token);
        await loadSnapshot(invitation.tenantId, token);
        if (live) { setState('ready'); setMessage('Secure holder portal ready.'); }
      } catch {
        if (live) { setPortalToken(''); setState('error'); setMessage(INVITATION_ERROR); }
      }
    })();
    return () => { live = false; };
  }, [loadSnapshot]);

  async function submitEvidence(event) {
    event.preventDefault();
    if (!portalToken || !tenantId || !selectedLifecycleId || !file || !privacyAccepted) return;
    setSubmitting(true);
    setMessage('Preparing and checking your evidence...');
    try {
      const contentSha256 = await sha256Hex(file);
      const headers = portalHeaders(tenantId, portalToken);
      const issue = await API.post('/v1/credential-renewal/portal/evidence-uploads', {
        lifecycleId: selectedLifecycleId,
        privacyClass,
        contentSha256,
        byteCount: file.size,
        idempotencyKey: idempotencyKey(),
      }, { headers });
      const uploadUrl = issue.data?.uploadUrl;
      const completionToken = issue.data?.completionToken;
      const uploadId = issue.data?.uploadId;
      const target = new URL(uploadUrl);
      if (target.protocol !== 'https:' || target.username || target.password || !completionToken || !uploadId) {
        throw new Error('invalid_upload_authority');
      }
      const uploaded = await fetch(target.toString(), {
        method:'PUT', body:file,
        headers:{ 'Content-Type':file.type || 'application/octet-stream' },
        credentials:'omit', redirect:'error', referrerPolicy:'no-referrer',
      });
      if (!uploaded.ok) throw new Error('upload_failed');
      await API.post(
        `/v1/credential-renewal/portal/evidence-uploads/${uploadId}/complete`,
        { completionToken }, { headers },
      );
      await loadSnapshot(tenantId, portalToken);
      setFile(null);
      setPrivacyAccepted(false);
      setMessage('Evidence received for authorised review. It is not marked verified.');
    } catch (error) {
      if (error.response?.status === 401) {
        setPortalToken(''); setState('error');
        setMessage('Your secure holder session has ended. Ask the requesting organisation for a new invitation.');
      } else {
        setMessage('The evidence was not accepted. Nothing was marked verified. Review the file and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const credentials = Array.isArray(snapshot?.credentials) ? snapshot.credentials : [];
  const requests = Array.isArray(snapshot?.requests) ? snapshot.requests : [];
  const evidence = Array.isArray(snapshot?.evidence) ? snapshot.evidence : [];
  const declarations = Array.isArray(snapshot?.declarations) ? snapshot.declarations : [];
  return <main className="crh-shell">
    <section className="crh-card" aria-labelledby="crh-title">
      <p className="crh-kicker">VISINVOKE Credential &amp; Renewal Manager</p>
      <h1 id="crh-title">Secure holder portal</h1>
      <p className="crh-release">NOT RELEASED / COMING SOON / DUE 2027</p>
      <div className={`crh-status crh-status--${state}`} role="status" aria-live="polite">{message}</div>
      {state === 'ready' && <>
        <section aria-labelledby="crh-credentials">
          <h2 id="crh-credentials">My credentials and status</h2>
          <p>Only records bound to this holder invitation are shown.</p>
          <div className="crh-grid">{credentials.map((item) => <button type="button" className="crh-record" key={item.id} aria-pressed={selectedLifecycleId === item.id} onClick={() => setSelectedLifecycleId(item.id)}><strong>{item.typeKey}</strong><span>{item.sourceState}</span><small>{item.lifecycleState}</small></button>)}</div>
          {!credentials.length && <p className="crh-empty">No credential records are assigned to this holder session.</p>}
        </section>
        <section className="crh-summary" aria-label="Request, declaration and evidence summary">
          <article><strong>{requests.length}</strong><span>renewal requests</span></article>
          <article><strong>{declarations.length}</strong><span>declarations</span></article>
          <article><strong>{evidence.length}</strong><span>evidence records</span></article>
        </section>
        <form className="crh-form" onSubmit={submitEvidence}>
          <h2>Submit requested evidence</h2>
          <p className="crh-boundary">Choose a credential card above. Your file is hashed locally, uploaded directly through a short-lived provider URL, checked for exact size/hash and malware status, then recorded for review. Receipt never means verification.</p>
          <label htmlFor="crh-file">Evidence file</label>
          <input id="crh-file" type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} required />
          <fieldset><legend>Privacy classification</legend><div className="crh-segments">{['standard','sensitive','identity','health'].map((value) => <button type="button" key={value} aria-pressed={privacyClass === value} onClick={() => setPrivacyClass(value)}>{value}</button>)}</div></fieldset>
          <div className="crh-privacy"><h3>Purpose and retention</h3><p>{snapshot?.privacyNotice}</p><p>The requesting organisation controls retention and lawful holds. Status-only roles do not receive sensitive evidence unless separately authorised.</p><label><input type="checkbox" checked={privacyAccepted} onChange={(event) => setPrivacyAccepted(event.target.checked)} />I understand the purpose and retention information.</label></div>
          <button className="crh-submit" type="submit" disabled={submitting || !selectedLifecycleId || !file || !privacyAccepted}>{submitting ? 'Submitting securely...' : 'Submit evidence securely'}</button>
        </form>
      </>}
    </section>
  </main>;
}

export { INVITATION_ERROR, portalHeaders, sha256Hex };
